import UploadIcon from 'imagesUwKluis/svg/import.svg';
import { bool, func, number, string, array } from 'prop-types';
import { useContext, useMemo } from 'react';
import { useTheme } from '@emotion/react';
import { useDropzone } from 'react-dropzone';
import {
  authorizedFileExtensions,
  pdfFileExtension,
} from '../../utils/authorizedFileExtensions';
import { Flex, Box, Stack } from './layouts';
import { useMediaQuery } from '@dnhb/node_package';
import { WithIcon } from './layouts';
import AddIcon from 'imagesUwKluis/svg/addfill.svg';
import Button from './Button';
import WhiteLabelContext from '../../context/WhiteLabelContext';
import {useTranslation} from "react-i18next";

const getIsDragRejectedMessage = (allowedFileExtensions, t) => {
  const types = [...allowedFileExtensions];

  if (types.length <= 1) {
    return t('Alleen {{type}} bestand(en) zijn toegestaan', {type: types[0]});
  }

  const lastItem = allowedFileExtensions.pop();

  const items = [
    allowedFileExtensions.join(', '),
    lastItem,
  ]

  const joinedItems = items.join(' ' + t('en') + ' ');
  return t('Alleen {{items}} bestandsextensies zijn toegestaan', {items: joinedItems});
}

const FileDropzoneV3 = ({ onDrop, disabled, loading, errors, multiple = true, allowedFileExtensions = [], ...boxProps }) => {
  const { t } = useTranslation();
  const { pdfAndImgOnly } = useContext(WhiteLabelContext);
  const generalAllowedFileExtensions = pdfAndImgOnly ? pdfFileExtension : authorizedFileExtensions
  allowedFileExtensions = allowedFileExtensions.length ? allowedFileExtensions : generalAllowedFileExtensions;
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: [...allowedFileExtensions, 'application/pdf'],
    disabled: disabled || loading,
    onDrop,
    multiple: multiple,
  });
  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56,25rem'})`
  );
  const {fillColor, borderColor, dragMessage} = useMemo(() => {
    const defaults = {
      borderColor: boxProps.borderColor,
      fillColor: boxProps.fillColor,
      dragMessage: t('Sleep bestanden hierheen of')
    }

    if (isDragActive && !isDragReject) {
      return {
        ...defaults,
        dragMessage: t('Laat het bestand los...'),
      }
    }

    if (isDragActive && isDragReject) {
      return {
        borderColor: 'alert',
        fillColor: 'alert',
        dragMessage: getIsDragRejectedMessage(allowedFileExtensions, t),
      }
    }

    return defaults;
  }, [boxProps.borderColor, boxProps.fillColor, isDragActive, isDragReject, allowedFileExtensions]);

  return (
    <Flex
      {...getRootProps({...boxProps, borderColor})}
      flexDirection="column"
      alignItems="center"
      fontWeight="bold"
      css={{ cursor: !loading ? 'pointer' : null }}
    >
      <input {...getInputProps()} />
      {loading && (
        <WithIcon color="primary" space="s">
          <UploadIcon width="40px" height="40px" />
          <span>{t('Aan het uploaden...')}</span>
        </WithIcon>
      )}
      {!loading && isMinDesktop && (
        <Box maxWidth="288px">
          <WithIcon
            css={{ display: 'flex', marginBottom: '8px' }}
            color={fillColor}
            space="s"
          >
            {!isDragReject && (<UploadIcon width="40px" height="40px" />)}
            <span>{dragMessage}</span>
          </WithIcon>
          {!loading && !isDragActive && (
            <Button
              width="100%"
              variant="primary-outline"
              buttonSize="small"
              fontScale="m"
              fontWeight="bold"
            >
              {t('Upload een bestand')}
            </Button>
          )}
        </Box>
      )}
      {!loading && !isMinDesktop && (
        <WithIcon color="primary" space="s">
          <AddIcon width="24" height="24" />
          <span>{t('Upload een bestand')}</span>
        </WithIcon>
      )}
      {errors.length > 0 && (
        <Stack space="s" mt="m">
          {errors.map((error, i) => (
            <Box key={i} color="primary" fontSize="s">
              {t('Er ging iets mis bij het uploaden van {{name}}', {name: error.name})}:
              {error.fileErrors.map((fileError) => (
                <Box fontWeight="normal" key={i + fileError}>
                  {fileError}
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      )}
    </Flex>
  );
};

FileDropzoneV3.propTypes = {
  border: string,
  borderColor: string,
  backgroundColor: string,
  justifyContent: string,
  flexDirection: string,
  alignItems: string,
  borderRadius: number,
  fontScale: string,
  textAlign: string,
  fillColor: string,
  padding: string,
  onDrop: func,
  disabled: bool,
  loading: bool,
  errors: array,
};

FileDropzoneV3.defaultProps = {
  border: 'dashed',
  borderColor: 'primary',
  backgroundColor: 'transparent',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
  borderRadius: 5,
  fontScale: 'm',
  fontWeight: 'bold',
  fillColor: 'primary',
  padding: '16px',
  onDrop: () => {},
  disabled: false,
  loading: false,
  errors: [],
};

export default FileDropzoneV3;
